import React, { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { useDraggable } from '@dnd-kit/core';
import { Button } from '@smartshell/ui-kit';
import cn from 'classnames';

import { EHostModalType } from 'components';
import { DEFAULT_GROUP_COLOR } from 'consts';
import { useDeleteHost } from 'hooks/useDeleteHost';
import { LocalizationContext } from 'localization';

import { openConfirmDialog } from '../../../../../services/Confirmation';
import { hostDragId } from '../../dragIds';
import {
  GetHostsResponse,
  HostGroupRequest,
  HostGroupResponse,
  HostRequest,
} from '../Host/interfaces';
import { GET_HOST, HOST_GROUP } from '../Host/schema';
import { getHostState } from '../Host/utiltes';
import { getHostTypeIcon } from '../utils';
import s from './DraggableMapHost.module.scss';
import { IDraggableMapHost } from './interfaces';

export const DraggableMapHost: FC<IDraggableMapHost> = ({
  host,
  disabled,
  id,
  onHostClick,
  hostModalsState,
}) => {
  const { t } = useContext(LocalizationContext);
  const deleteHost = useDeleteHost(t);

  const { isDragging, setNodeRef, listeners } = useDraggable({
    id: hostDragId(id),
    disabled,
  });
  const { data: hostResponse } = useQuery<GetHostsResponse, HostRequest>(
    GET_HOST,
    {
      fetchPolicy: 'cache-only',
      variables: { id },
    }
  );

  const { data: groupResponse } = useQuery<HostGroupResponse, HostGroupRequest>(
    HOST_GROUP,
    {
      fetchPolicy: 'cache-only',
      variables: { id: hostResponse?.hostOverview.group_id ?? 0 },
      skip: !hostResponse?.hostOverview,
    }
  );

  if (!hostResponse?.hostOverview) return null;

  const hostState = getHostState(hostResponse.hostOverview);

  const { position } = hostResponse.hostOverview;

  const cardColor =
    hostState?.color ||
    groupResponse?.hostGroupOverview.customization?.color ||
    DEFAULT_GROUP_COLOR;

  return (
    <div className={s.wrapper}>
      <div className={cn(s.hover, disabled && s.hover_hidden)}>
        <Button
          onlyIcon
          onClick={(e) => {
            e.stopPropagation();
            hostModalsState.setSelectedHostsIds([id]);
            hostModalsState.setType(EHostModalType.MANAGE_HOST);
          }}
          className={s.button}
          iconName="EditIcon"
        />
        <Button
          onlyIcon
          onClick={(e) => {
            e.stopPropagation();
            openConfirmDialog(t, 'remove', {
              message: t(
                'host.delete.confirm.message',
                hostResponse?.hostOverview?.alias
              ),
            }).then(async ({ status }) => {
              if (status === 'DECLINED') {
                return;
              }
              deleteHost({
                variables: { id },
              });
            });
          }}
          className={cn(
            s.button,
            hostResponse?.hostOverview?.in_service && s.disabled
          )}
          iconName="BucketIcon"
        />
      </div>
      <div
        data-host-id={id}
        data-draggable-id={hostDragId(id)}
        onClick={(event) => {
          onHostClick(event, id as number);
        }}
        style={{
          opacity: isDragging ? 0 : 1,
          cursor: disabled ? 'pointer' : 'grab',
          // @ts-ignore it's ok to set css var in style
          '--group-color': cardColor,
          '--group-color-10': cardColor + '1A',
          '--group-color-20': cardColor + '33',
        }}
        ref={setNodeRef}
        {...listeners}
        className={s.container}
      >
        <span className={s.number}>{position}</span>
        <div className={s.icon}>{getHostTypeIcon(host?.hostType)}</div>
      </div>
    </div>
  );
};
